/**
 * Profile Questionnaire — B-2.C Skills / Preferences section.
 *
 * Pure view for the skills picker. Renders the canonical
 * server-supplied taxonomy (popular-first order preserved), supports
 * search and category filter, multi-select chips with remove
 * affordance, max-10 enforcement, and a save-failure error surface.
 *
 * No business logic. No API calls. No `useState` for domain state.
 * The reducer in `update.ts` is the only state-mutation site; this
 * view only renders and dispatches.
 *
 * FAKE_DURABILITY = 0: nothing here is persisted outside the server
 * response.
 */

import type { ReactNode } from 'react';
import type { ProfileFormModel } from './model';
import { MAX_SELECTED_SKILLS, type Skill } from '@/api/skills';

export interface SkillsSectionProps {
  readonly model: ProfileFormModel;
  readonly onSkillSelected: (skillId: string) => void;
  readonly onSkillRemoved: (skillId: string) => void;
  readonly onSearchChanged: (value: string) => void;
  readonly onCategoryChanged: (value: string | null) => void;
  readonly onRetryTaxonomy: () => void;
  readonly onRetryLoadSelection: () => void;
  readonly onRetrySave: () => void;
  readonly onSaveSelection: () => void;
  readonly disabled: boolean;
}

const NO_CATEGORY: string | null = null;

function uniqueCategories(items: readonly Skill[]): readonly string[] {
  const seen = new Set<string>();
  for (const s of items) {
    seen.add(s.category);
  }
  return Array.from(seen).sort();
}

function filteredSkills(
  items: readonly Skill[],
  search: string,
  category: string | null
): readonly Skill[] {
  const q = search.trim().toLowerCase();
  const out: Skill[] = [];
  for (const s of items) {
    if (!s.is_active) continue;
    if (category !== null && s.category !== category) continue;
    if (
      q.length > 0 &&
      !s.label_ru.toLowerCase().includes(q) &&
      !s.code.toLowerCase().includes(q)
    ) {
      continue;
    }
    out.push(s);
  }
  return out;
}

export function SkillsSection(props: SkillsSectionProps): ReactNode {
  const { model } = props;
  const taxonomy = model.skillsTaxonomy;
  const selectedIds = model.selectedSkillIds;
  const atMax = selectedIds.length >= MAX_SELECTED_SKILLS;

  return (
    <section
      className="pq-section pq-skills-section"
      data-testid="pq-skills-section"
      aria-labelledby="pq-skills-title"
    >
      <h2 id="pq-skills-title" className="pq-h2">Интересы</h2>
      <p className="pq-lede-small">
        Выберите до {MAX_SELECTED_SKILLS} интересов — это поможет находить
        вас в подборках. Можно не выбирать ничего.
      </p>

      {taxonomy.status === 'loading' && (
        <p className="pq-skills-loading" data-testid="pq-skills-loading">
          Загрузка списка интересов…
        </p>
      )}

      {taxonomy.status === 'failed' && (
        <div className="pq-skills-error" role="alert" data-testid="pq-skills-error">
          <p>{taxonomy.error}</p>
          <button
            type="button"
            className="pq-btn-secondary"
            onClick={props.onRetryTaxonomy}
            data-testid="pq-skills-retry-taxonomy"
          >
            Попробовать снова
          </button>
        </div>
      )}

      {taxonomy.status === 'ready' && (
        <SkillsPicker
          taxonomy={taxonomy.items}
          search={model.skillsSearch}
          category={model.skillsCategory}
          selectedIds={selectedIds}
          atMax={atMax}
          disabled={props.disabled || model.skillsSave === 'saving'}
          onSkillSelected={props.onSkillSelected}
          onSkillRemoved={props.onSkillRemoved}
          onSearchChanged={props.onSearchChanged}
          onCategoryChanged={props.onCategoryChanged}
        />
      )}

      <SelectedChips
        items={taxonomy.status === 'ready' ? taxonomy.items : []}
        selectedIds={selectedIds}
        onRemove={props.onSkillRemoved}
      />

      <p
        className="pq-skills-count"
        data-testid="pq-skills-count"
        aria-live="polite"
      >
        Выбрано: {selectedIds.length}/{MAX_SELECTED_SKILLS}
      </p>

      {/*
        Real-FE persistence affordance. Without this, the user has
        no way to commit a skills change through the production FE
        program (the only path was the SubmitSucceeded cascade,
        which requires also dirty profile fields). The submit
        button is disabled while saving; the save-failed branch
        surfaces a separate retry button (pq-skills-retry-save).
      */}
      <div className="pq-skills-save-row">
        <button
          type="button"
          className="pq-btn-primary pq-skills-save"
          onClick={props.onSaveSelection}
          disabled={
            props.disabled ||
            model.skillsSave === 'saving' ||
            (model.mode.kind === 'edit' &&
              selectedIds.length === model.persistedSkillIds.length &&
              selectedIds.every((id, i) => id === model.persistedSkillIds[i]))
          }
          data-testid="pq-skills-save"
          aria-busy={model.skillsSave === 'saving'}
        >
          {model.skillsSave === 'saving'
            ? 'Сохраняем...'
            : model.mode.kind === 'edit'
            ? 'Сохранить интересы'
            : 'Сохранить'}
        </button>
      </div>

      {model.skillsSave === 'failed' && (
        <div className="pq-skills-save-error" role="alert" data-testid="pq-skills-save-error">
          <p>{model.skillsError ?? 'Не удалось сохранить интересы'}</p>
          <button
            type="button"
            className="pq-btn-secondary"
            onClick={props.onRetrySave}
            data-testid="pq-skills-retry-save"
          >
            Повторить сохранение
          </button>
        </div>
      )}

      {model.skillsSave === 'failed' && (
        <p className="pq-skills-retain-note" data-testid="pq-skills-retain-note">
          Ваш выбор сохранён в форме. Повторите сохранение или обновите
          страницу, чтобы восстановить серверные данные.
        </p>
      )}

      {model.mode.kind === 'edit' &&
        model.skillsError !== null &&
        taxonomy.status !== 'failed' && (
          <p className="pq-skills-load-error" data-testid="pq-skills-load-error">
            {model.skillsError}
            <button
              type="button"
              className="pq-btn-secondary pq-skills-retry-load"
              onClick={props.onRetryLoadSelection}
              data-testid="pq-skills-retry-load"
            >
              Повторить
            </button>
          </p>
        )}
    </section>
  );
}

interface SkillsPickerProps {
  readonly taxonomy: readonly Skill[];
  readonly search: string;
  readonly category: string | null;
  readonly selectedIds: readonly string[];
  readonly atMax: boolean;
  readonly disabled: boolean;
  readonly onSkillSelected: (skillId: string) => void;
  readonly onSkillRemoved: (skillId: string) => void;
  readonly onSearchChanged: (value: string) => void;
  readonly onCategoryChanged: (value: string | null) => void;
}

function SkillsPicker(props: SkillsPickerProps): ReactNode {
  const categories = uniqueCategories(props.taxonomy);
  const visible = filteredSkills(props.taxonomy, props.search, props.category);
  return (
    <div className="pq-skills-picker" data-testid="pq-skills-picker">
      <div className="pq-skills-controls">
        <input
          type="search"
          className="pq-input pq-skills-search"
          placeholder="Поиск интересов"
          value={props.search}
          onChange={(e) => props.onSearchChanged(e.currentTarget.value)}
          disabled={props.disabled}
          data-testid="pq-skills-search"
          aria-label="Поиск интересов"
        />
        <select
          className="pq-select pq-skills-category"
          value={props.category ?? ''}
          onChange={(e) =>
            props.onCategoryChanged(
              e.currentTarget.value === ''
                ? NO_CATEGORY
                : e.currentTarget.value
            )
          }
          disabled={props.disabled}
          data-testid="pq-skills-category"
          aria-label="Категория"
        >
          <option value="">Все категории</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      {visible.length === 0 ? (
        <p className="pq-skills-empty" data-testid="pq-skills-empty">
          Ничего не найдено.
        </p>
      ) : (
        <ul className="pq-skills-list" data-testid="pq-skills-list">
          {visible.map((s) => {
            const isSelected = props.selectedIds.includes(s.id);
            return (
              <li key={s.id} className="pq-skills-list-item">
                <button
                  type="button"
                  className={
                    'pq-skills-item' +
                    (isSelected ? ' pq-skills-item-selected' : '')
                  }
                  onClick={() =>
                    isSelected
                      ? props.onSkillRemoved(s.id)
                      : props.onSkillSelected(s.id)
                  }
                  disabled={props.disabled || (!isSelected && props.atMax)}
                  data-testid="pq-skills-item"
                  data-skill-id={s.id}
                  data-selected={isSelected ? 'true' : 'false'}
                  aria-pressed={isSelected}
                  aria-label={s.label_ru}
                >
                  {s.label_ru}
                  <span className="pq-skills-item-code">{s.code}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface SelectedChipsProps {
  readonly items: readonly Skill[];
  readonly selectedIds: readonly string[];
  readonly onRemove: (skillId: string) => void;
}

function SelectedChips(props: SelectedChipsProps): ReactNode {
  if (props.selectedIds.length === 0) {
    return (
      <p className="pq-skills-chips-empty" data-testid="pq-skills-chips-empty">
        Ничего не выбрано.
      </p>
    );
  }
  const labels = new Map<string, Skill>();
  for (const s of props.items) labels.set(s.id, s);
  return (
    <ul className="pq-skills-chips" data-testid="pq-skills-chips">
      {props.selectedIds.map((id) => {
        const skill = labels.get(id);
        const label = skill?.label_ru ?? id;
        return (
          <li key={id} className="pq-skills-chip">
            <span className="pq-skills-chip-label">{label}</span>
            <button
              type="button"
              className="pq-skills-chip-remove"
              onClick={() => props.onRemove(id)}
              data-testid="pq-skills-chip-remove"
              data-skill-id={id}
              aria-label={`Удалить ${label}`}
            >
              ×
            </button>
          </li>
        );
      })}
    </ul>
  );
}
